import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { SchemaDeliveryCard } from './SchemaDeliveryCard';

type Bucket = 'schema' | 'meta-header' | 'citation' | 'content';

interface Props {
  domainId: string;
  initialTab?: string;
  onBack: () => void;
}

interface FixItem {
  id: string;
  fixPayload: Record<string, unknown>;
  editedPayload: Record<string, unknown> | null;
  editedAt: string | null;
  methodologyRef: string;
  customerApprovedAt: string | null;
  createdAt: string;
  schemaType?: string;
  fixType?: string;
  fixCategory?: string;
  pillar?: string;
  platform?: string;
  actionType?: string;
}

const TABS: { id: Bucket; label: string }[] = [
  { id: 'schema', label: 'Schema' },
  { id: 'meta-header', label: 'Meta / headers' },
  { id: 'citation', label: 'Citations' },
  { id: 'content', label: 'Content' },
];

function isReviewableBucket(id: string): id is Bucket {
  return id === 'schema' || id === 'meta-header' || id === 'citation' || id === 'content';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function FixCard({
  fix,
  bucket,
  onApprove,
  onReject,
  onEdit,
  isPending,
  isEditPending,
}: {
  fix: FixItem;
  bucket: Bucket;
  onApprove: () => void;
  onReject: () => void;
  onEdit: (payload: Record<string, unknown>) => void;
  isPending: boolean;
  isEditPending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const isActioned = !!fix.customerApprovedAt;
  const hasEdits = !!fix.editedPayload;
  const activePayload = fix.editedPayload ?? fix.fixPayload;

  const title =
    bucket === 'schema'
      ? `Schema fix — ${fix.schemaType ?? 'Unknown type'}`
      : bucket === 'meta-header'
      ? `${fix.fixCategory === 'http_header' ? 'Header' : 'Meta'} — ${fix.fixType ?? 'Unknown type'}`
      : `Citation — ${fix.platform ?? 'Unknown platform'}`;

  const subtitle =
    bucket === 'citation'
      ? fix.actionType?.replace('_', ' ')
      : fix.pillar
      ? fix.pillar.toUpperCase()
      : undefined;

  function openEditor() {
    setEditText(JSON.stringify(activePayload, null, 2));
    setParseError(null);
    setIsEditing(true);
  }

  function handleSave() {
    let parsed: Record<string, unknown>;
    try {
      const value: unknown = JSON.parse(editText);
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        setParseError('Payload must be a JSON object, not an array or primitive.');
        return;
      }
      parsed = value as Record<string, unknown>;
    } catch {
      setParseError('Invalid JSON — please fix the syntax and try again.');
      return;
    }
    setParseError(null);
    onEdit(parsed);
    setIsEditing(false);
  }

  function handleCancel() {
    setIsEditing(false);
    setParseError(null);
  }

  return (
    <div
      className={`bg-white rounded-[6px] p-6 transition-shadow ${
        isActioned ? 'opacity-60' : 'hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]'
      }`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-[#0F172A]">{title}</p>
            {subtitle && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#475569] bg-[#F1F5F9] px-2 py-0.5 rounded">
                {subtitle}
              </span>
            )}
            {isActioned && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#1D9E75] bg-green-50 px-2 py-0.5 rounded">
                Approved
              </span>
            )}
            {hasEdits && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#7C3AED] bg-violet-50 px-2 py-0.5 rounded">
                Edited
              </span>
            )}
          </div>
          <p className="text-xs text-[#94A3B8] mt-1">
            Generated {formatDate(fix.createdAt)} · <span className="font-mono">{fix.methodologyRef}</span>
            {fix.editedAt && (
              <> · Edited {formatDate(fix.editedAt)}</>
            )}
          </p>
        </div>

        {!isEditing && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-[#475569] hover:text-[#0F172A] shrink-0 transition-colors"
            aria-expanded={expanded}
          >
            {expanded ? 'Hide' : 'View payload'}
          </button>
        )}
      </div>

      {/* Payload preview / editor */}
      {expanded && !isEditing && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#475569]">
              {hasEdits ? 'Edited payload (will be deployed)' : 'Proposed payload'}
            </p>
            {!isActioned && (
              <button
                onClick={openEditor}
                className="text-xs text-[#7C3AED] hover:text-[#6D28D9] font-medium transition-colors"
              >
                Edit
              </button>
            )}
          </div>
          <pre className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-[6px] p-4 text-xs text-[#0F172A] overflow-x-auto">
            {JSON.stringify(activePayload, null, 2)}
          </pre>
          {hasEdits && (
            <details className="mt-2">
              <summary className="text-xs text-[#94A3B8] cursor-pointer hover:text-[#475569]">
                Show original proposal
              </summary>
              <pre className="mt-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[6px] p-4 text-xs text-[#94A3B8] overflow-x-auto opacity-60">
                {JSON.stringify(fix.fixPayload, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {isEditing && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-[#475569] mb-2">
            Edit payload — changes will be deployed instead of the original
          </p>
          <textarea
            value={editText}
            onChange={(e) => {
              setEditText(e.target.value);
              setParseError(null);
            }}
            rows={Math.min(30, editText.split('\n').length + 2)}
            className="w-full font-mono text-xs bg-[#F8FAFC] border border-[#E2E8F0] rounded-[6px] p-4 text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] resize-y"
            spellCheck={false}
          />
          {parseError && (
            <p className="text-xs text-red-500 mt-1">{parseError}</p>
          )}
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={handleSave}
              disabled={isEditPending}
              className="bg-[#7C3AED] text-white text-xs font-medium px-4 py-2 rounded-[6px] hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
            >
              {isEditPending ? 'Saving...' : 'Save changes'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isEditPending}
              className="text-xs font-medium px-4 py-2 rounded-[6px] border border-[#E2E8F0] text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!isActioned && !isEditing && (
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={onApprove}
            disabled={isPending}
            className="bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-[6px] hover:bg-[#0F6E56] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
          >
            {isPending ? 'Saving...' : 'Approve'}
          </button>
          <button
            onClick={onReject}
            disabled={isPending}
            className="text-sm font-medium px-4 py-2 rounded-[6px] border border-[#E2E8F0] text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

function BucketTab({
  bucket,
  domainId,
}: {
  bucket: Bucket;
  domainId: string;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['afi-fixes', domainId, bucket],
    queryFn: () => api.afi.listFixes(domainId, bucket),
    staleTime: 30_000,
    retry: 1,
  });

  const approveMutation = useMutation({
    mutationFn: (fixId: string) => api.afi.approveFix(bucket, fixId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['afi-fixes', domainId, bucket] });
      void queryClient.invalidateQueries({ queryKey: ['afi-pending-fixes', domainId] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (fixId: string) => api.afi.rejectFix(bucket, fixId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['afi-fixes', domainId, bucket] });
      void queryClient.invalidateQueries({ queryKey: ['afi-pending-fixes', domainId] });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ fixId, payload }: { fixId: string; payload: Record<string, unknown> }) =>
      api.afi.editFix(bucket, fixId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['afi-fixes', domainId, bucket] });
    },
  });

  const actioningId =
    approveMutation.isPending ? (approveMutation.variables as string) :
    rejectMutation.isPending ? (rejectMutation.variables as string) :
    null;

  const editingId = editMutation.isPending
    ? (editMutation.variables as { fixId: string }).fixId
    : null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-[6px] p-6 animate-pulse">
            <div className="h-4 w-48 bg-gray-100 rounded mb-2" />
            <div className="h-3 w-64 bg-gray-100 rounded mb-4" />
            <div className="flex gap-3">
              <div className="h-9 w-24 bg-gray-200 rounded-[6px]" />
              <div className="h-9 w-20 bg-gray-100 rounded-[6px]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-[6px] p-10 text-center">
        <p className="text-sm font-medium text-[#0F172A]">Could not load fixes</p>
        <p className="text-xs text-[#94A3B8] mt-1">There was a problem fetching this queue. Try refreshing the page.</p>
      </div>
    );
  }

  const fixes = (data?.fixes ?? []) as FixItem[];
  const pending = fixes.filter((f) => !f.customerApprovedAt);
  const actioned = fixes.filter((f) => !!f.customerApprovedAt);

  if (fixes.length === 0) {
    return (
      <div className="bg-white rounded-[6px] p-10 text-center">
        <p className="text-sm font-medium text-[#0F172A]">No fixes in this bucket</p>
        <p className="text-xs text-[#94A3B8] mt-1">
          Fixes appear here after an audit generates them.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Schema needs raw-HTML delivery to reach non-JS AI crawlers — show the
          paste block once at least one schema fix is approved. */}
      {bucket === 'schema' && actioned.length > 0 && (
        <SchemaDeliveryCard domainId={domainId} />
      )}

      {pending.map((fix) => (
        <FixCard
          key={fix.id}
          fix={fix}
          bucket={bucket}
          onApprove={() => approveMutation.mutate(fix.id)}
          onReject={() => rejectMutation.mutate(fix.id)}
          onEdit={(payload) => editMutation.mutate({ fixId: fix.id, payload })}
          isPending={actioningId === fix.id}
          isEditPending={editingId === fix.id}
        />
      ))}

      {actioned.length > 0 && (
        <>
          <p className="text-xs text-[#94A3B8] font-semibold uppercase tracking-widest pt-2">
            Already approved
          </p>
          {actioned.map((fix) => (
            <FixCard
              key={fix.id}
              fix={fix}
              bucket={bucket}
              onApprove={() => {}}
              onReject={() => {}}
              onEdit={() => {}}
              isPending={false}
              isEditPending={false}
            />
          ))}
        </>
      )}
    </div>
  );
}

export function AfiReviewQueuePage({ domainId, initialTab, onBack }: Props) {
  const defaultTab = isReviewableBucket(initialTab ?? '') ? (initialTab as Bucket) : 'schema';
  const [activeTab, setActiveTab] = useState<Bucket>(defaultTab);

  // Sync tab to URL search param for bookmarkability
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', activeTab);
    window.history.replaceState({}, '', url.toString());
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-[#F1F5F9] p-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-xs text-[#475569] hover:text-[#0F172A] transition-colors mb-3 flex items-center gap-1"
        >
          ← Back to overview
        </button>
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#1D9E75] mb-1">
          Auto-fix
        </p>
        <h1 className="text-2xl font-semibold text-[#0F172A]">Review queue</h1>
        <p className="text-sm text-[#475569] mt-1">
          Review, edit if needed, then approve or reject generated fixes before they are deployed.
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-[#E2E8F0] mb-6 flex gap-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-[#0F6E56] text-[#0F172A]'
                : 'border-transparent text-[#475569] hover:text-[#0F172A]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <BucketTab bucket={activeTab} domainId={domainId} />
    </div>
  );
}
